import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import {toStrictSchemaWithDynamicParameters} from "#material/nodes/base/material/schema"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import type {InterpolationType} from "#material/nodes/color/color-ramp/types"
import {interpolationTypeSchema} from "#material/nodes/color/color-ramp/types"

export interface ColorRampSockets extends MaterialNodeSockets {
    inputs: {
        fac: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
    }
    settings: {
        alpha: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        colorRampColorMode: "string" // found in inputs: -; found in settings: string; inferred from data: string
        colorRampCyclesRampAlphaTable: "floatArray" // found in inputs: -; found in settings: float_array; inferred from data: floatArray
        colorRampCyclesRampColorTable: "colorArray" // found in inputs: -; found in settings: vector_array; inferred from data: float3Array
        colorRampHueInterpolation: "string" // found in inputs: -; found in settings: string; inferred from data: string
        fac: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
    }
    settingsExtensions: Partial<
        Record<`colorRampElements_${number}Alpha`, number> & // colorRampElements_0Alpha: "float" found in inputs: -; found in settings: float; inferred from data: float
            Record<`colorRampElements_${number}Color_${number}`, number> & // colorRampElements_0Color_0: "float" found in inputs: -; found in settings: float; inferred from data: float
            Record<`colorRampElements_${number}Position`, number> // colorRampElements_0Position: "float" found in inputs: -; found in settings: float; inferred from data: float
    > & {
        colorRampInterpolation?: InterpolationType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        color: "color"
        alpha: "float"
    }
}

export const colorRampDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeValToRGB,
    },
    nodeClass: "ColorRamp",
    validation: {
        returns: z.strictObject({color: materialColorOutput, alpha: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                fac: optionalMaterialInput,
            }),
            settings: toStrictSchemaWithDynamicParameters(
                z.object({
                    alpha: z.optional(z.number()),
                    color: z.optional(color),
                    colorRampColorMode: z.optional(z.string()),
                    colorRampCyclesRampAlphaTable: z.optional(z.array(z.number())),
                    colorRampCyclesRampColorTable: z.optional(z.array(color)),
                    colorRampHueInterpolation: z.optional(z.string()),
                    colorRampInterpolation: z.optional(interpolationTypeSchema),
                    fac: z.optional(z.number()),
                }),
                [
                    {
                        regex: /^colorRampElements_(\d+)Alpha$/,
                        schema: z.number(),
                    },
                    {
                        regex: /^colorRampElements_(\d+)Color_(\d+)$/,
                        schema: z.number(),
                    },
                    {
                        regex: /^colorRampElements_(\d+)Position$/,
                        schema: z.number(),
                    },
                ],
            ),
            ui: materialUiSchema,
        }),
    },
    ui: {
        label: "Color Ramp",
        color: "#6e5eb2",
        width: 240,
        items: [
            {name: "color", label: "Color", valueType: "color", output: true},
            {name: "alpha", label: "Alpha", valueType: "float", output: true},
            {custom: true, componentClass: "colorRamp"},
            {
                defaultValue: "LINEAR",
                name: "colorRampInterpolation",
                label: "Interpolation",
                valueType: "string",
                options: [
                    {label: "Linear", value: "LINEAR"},
                    {label: "Ease", value: "EASE"},
                    {label: "Cardinal", value: "CARDINAL"},
                    {label: "B-Spline", value: "B_SPLINE"},
                    {label: "Constant", value: "CONSTANT"},
                ],
                input: false,
                setting: true,
            },
            {name: "fac", label: "Fac", valueType: "float", defaultValue: 0, range: {min: 0, max: 1}, input: true},
        ],
        support: {
            cycles: {
                full: false,
                remark: "Approximate implementation. Linear interpolation only.",
            },
            three: {
                full: false,
                remark: "Approximate implementation. Linear interpolation only.",
            },
        },
    },
} satisfies MaterialNodeDefinition<ColorRampSockets>
